import { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Heart,
  LayoutDashboard,
  Calendar,
  Pill,
  FileText,
  MessageSquare,
  CreditCard,
  User,
  LogOut,
  Menu,
  X,
} from 'lucide-react'
import { Button } from '@components/ui/button'
import { usePortalAuthStore } from '@hooks/usePortalAuthStore'
import { cn } from '@lib/utils'

const navItems = [
  { name: 'Inicio', href: '/portal/dashboard', icon: LayoutDashboard },
  { name: 'Citas', href: '/portal/appointments', icon: Calendar },
  { name: 'Recetas', href: '/portal/prescriptions', icon: Pill },
  { name: 'Expediente', href: '/portal/records', icon: FileText },
  { name: 'Mensajes', href: '/portal/messages', icon: MessageSquare },
  { name: 'Facturación', href: '/portal/billing', icon: CreditCard },
  { name: 'Perfil', href: '/portal/profile', icon: User },
]

export function PortalLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { logout, user } = usePortalAuthStore()

  const handleLogout = () => {
    logout()
    navigate('/portal/login')
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-16 items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <Link to="/portal/dashboard" className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-xl gradient-medical flex items-center justify-center">
                <Heart className="h-5 w-5 text-white" />
              </div>
              <span className="font-semibold text-lg hidden sm:block">Portal del paciente</span>
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                Bienvenido, {user?.first_name}
              </span>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar sesión
              </Button>
            </div>
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-30 w-64 transform border-r bg-background transition-transform duration-200 ease-in-out md:translate-x-0 md:static md:block",
            mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="flex h-full flex-col pt-20 md:pt-4">
            <nav className="flex-1 space-y-1 px-3">
              {navItems.map((item) => {
                const isActive = location.pathname === item.href
                const Icon = item.icon
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-ml-primary text-white"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {item.name}
                  </Link>
                )
              })}
            </nav>

            <div className="border-t p-4 md:hidden">
              <Button variant="outline" className="w-full" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar sesión
              </Button>
            </div>
          </div>
        </aside>

        {mobileMenuOpen && (
          <div
            className="fixed inset-0 z-20 bg-black/50 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        <main className="flex-1 p-4 lg:p-8">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
